import Swal from "sweetalert2";
const TIMEZONE_MAP = {
  "MST": "America/Denver",
  "PST": "America/Los_Angeles",
  "EST": "America/New_York",
  "CST": "America/Chicago",
  // Add more mappings as needed
};

/**
 * Main function: API first, fallback to fixed hours
 */
export const isStoreOpen = (hours) => {
  if (hours && hours.time_zone) {
    return isStoreOpenAPI(hours);  // use API logic
  }
  return isStoreOpenFallback();    // fallback to your static logic
};

/* ---------------------------------------------------------
   1. API VERSION (same structure as your other restaurants)
--------------------------------------------------------- */
const isStoreOpenAPI = (hours) => {
  const timeZone = TIMEZONE_MAP[hours.time_zone] || "America/Denver";

  const now = new Date(
    new Date().toLocaleString("en-US", { timeZone })
  );

  const dayName = now
    .toLocaleString("en-US", { timeZone, weekday: "long" })
    .toLowerCase();

  const todayDate = new Date().toLocaleDateString("en-CA", { timeZone });

  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const nowStr = now.toLocaleTimeString("en-US", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  const nowMin = toMin(nowStr);

  /* -----------------------------------------
     SPECIAL HOURS FROM API
  ------------------------------------------ */
  const special = hours.specialhours?.find((s) => s.date === todayDate);

  if (special) {
    if (special.is_closed) return false;

    if (special.open_time && special.close_time) {
      const openMin = toMin(special.open_time);
      const closeMin = toMin(special.close_time);
      return nowMin >= openMin && nowMin <= closeMin;
    }
  }

  /* -----------------------------------------
     REGULAR API HOURS
  ------------------------------------------ */
  const raw = hours[dayName];
  if (!raw || raw.toLowerCase() === "closed") return false;

  const [range, breakRange] = raw.split("|");
  const [openStr, closeStr] = range.split("-");

  const openMin = toMin(openStr);
  const closeMin = toMin(closeStr);

  if (nowMin < openMin || nowMin > closeMin) return false;

  if (breakRange) {
    const [bStart, bEnd] = breakRange.split("-");
    const bStartMin = toMin(bStart);
    const bEndMin = toMin(bEnd);

    if (nowMin >= bStartMin && nowMin < bEndMin) return false;
  }

  return true;
};

/* ---------------------------------------------------------
   2. FALLBACK LOGIC (your old Denver schedule)
--------------------------------------------------------- */
const isStoreOpenFallback = () => {
  const now = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Denver" })
  );

  const day = now.getDay(); // 2 = Tuesday

  // CLOSED TUESDAY rule
  if (day === 2) return false;

  const startTime = "15:00"; // 3 PM
  const endTime = "21:45";   // 9:45 PM

  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const nowStr = now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });

  const nowMin = toMin(nowStr);
  const startMin = toMin(startTime);
  const endMin = toMin(endTime);

  return nowMin >= startMin && nowMin <= endMin;
};

/* ---------------------------------------------------------
   3. ERROR ALERT (API first, fallback second)
--------------------------------------------------------- */
export const showTimeError = (hours) => {
  if (hours && hours.time_zone) {
    return showTimeErrorAPI(hours);
  }

  return Swal.fire({
    icon: "error",
    title: "We’re currently closed.",
    html: `
      <strong>Hours:</strong><br>
      Monday, Wednesday–Sunday<br>
      3:00 PM – 9:45 PM<br>
      <em>Closed on Tuesdays</em>
    `,
    confirmButtonText: "OK",
    confirmButtonColor: "#a6e01e",
  });
};

/* ---------------------------------------------------------
   4. API ERROR MODAL
--------------------------------------------------------- */
const showTimeErrorAPI = (hours) => {
  const days = [
    "monday", "tuesday", "wednesday",
    "thursday", "friday", "saturday", "sunday",
  ];

  const formatted = days
    .map((d) => {
      const label = d.charAt(0).toUpperCase() + d.slice(1);
      const raw = hours[d];
      if (!raw || raw.toLowerCase() === "closed") return `${label}: CLOSED`;

      const [range, br] = raw.split("|");
      return `${label}: ${range}${br ? ` (Break: ${br})` : ""}`;
    })
    .join("<br>");

  Swal.fire({
    icon: "error",
    title: "We’re currently closed.",
    html: `
      <strong>Hours:</strong><br>
      ${formatted}<br><br>
      <strong>Time Zone:</strong> ${hours.time_zone}
    `,
    confirmButtonColor: "#a6e01e",
  });
};

/**
 * Check if it’s lunch time (Denver)
 * Returns true between 3:00 PM and 5:00 PM, weekdays only (Mon–Fri).
 */
export const checkIfLunchTime = () => {
  const nowInDenver = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Denver" })
  );
  const day = nowInDenver.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

  // Skip weekends and Tuesday (closed day)
  if (day === 0 || day === 6 || day === 2) return false;

  const currentTime = nowInDenver
    .toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    })
    .trim();

  const toMinutes = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const start = toMinutes("15:00");
  const end = toMinutes("17:00");
  const current = toMinutes(currentTime);

  return current >= start && current <= end;
};
